import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import * as Sentry from "@sentry/react";
import { identifyPosthogUser } from '../utils/posthogIdentity';

import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarProvider,
    SidebarRail,
    SidebarTrigger,
    useSidebar
} from "@/components/ui/sidebar"

import { Input } from "@/components/ui/input"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"

import { Separator } from "@/components/ui/separator"
import { useI18n } from '../hooks/useI18n';
import {
    DivideIcon,
    ArrowUpOnSquareIcon,
    NewspaperIcon,
    BuildingOfficeIcon,
    FolderIcon,
    BuildingStorefrontIcon,
    ArrowRightOnRectangleIcon,
    UsersIcon,
    CubeIcon,
    LinkIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CurrencyDollarIcon,
    MagnifyingGlassIcon,
    BanknotesIcon,
    UserIcon,
    Cog6ToothIcon,
    ChartBarIcon,
    ArchiveBoxIcon,
    PlusIcon,
    ClipboardDocumentListIcon,
    ArrowsRightLeftIcon,
    BeakerIcon,
    ScaleIcon,
    ArchiveBoxArrowDownIcon,
    TruckIcon,
    ArrowDownTrayIcon,
    ShoppingCartIcon,
    BookOpenIcon,
    CpuChipIcon,
    ClockIcon,
    BriefcaseIcon,
    SunIcon,
    UserGroupIcon,
    CalendarIcon,
    TagIcon
} from '@heroicons/react/24/outline';
import { HandCoinsIcon, UsersFourIcon, VectorTwo, CoffeeIcon } from '@phosphor-icons/react';
import { logout, apiFetch } from '../utils/api';
import { ChartNoAxesCombinedIcon } from 'lucide-react';
import NotificationInbox from './NotificationInbox';


interface User {
    id?: string;
    email: string;
    full_name?: string;
    is_superuser: boolean;
    cost_center_id?: string;
    organization_id?: string;
    organization_name?: string;
    roles?: string[];
    permissions?: string[];
    // Module access flags
    module_people_enabled?: boolean;
    module_inventory_enabled?: boolean;
    module_menus_enabled?: boolean;
    modules?: string[];
}

interface NavigationItem {
    name: string;
    href: string;
    icon: React.ComponentType<any>;
}

interface CollapsibleSidebarSectionProps {
    title: string;
    icon: React.ComponentType<any>;
    items: NavigationItem[];
    defaultExpanded?: boolean;
}

interface SidebarItemProps {
    title: string;
    icon: React.ComponentType<any>;
    items: NavigationItem[];
}

function CollapsibleSidebarSection({
    title,
    icon: IconComponent,
    items,
    defaultExpanded = true
}: CollapsibleSidebarSectionProps) {
    const navigate = useNavigate();
    const routerState = useRouterState();
    const currentPath = routerState.location.pathname;
    const { state } = useSidebar();

    // Create a unique key for this section based on title
    const sectionKey = `sidebar-section-${title}`;

    // Initialize state from sessionStorage or use default
    const [isExpanded, setIsExpanded] = useState(() => {
        const saved = sessionStorage.getItem(sectionKey);
        if (saved !== null) {
            return saved === 'true';
        }
        return defaultExpanded;
    });

    // Save state to sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem(sectionKey, isExpanded.toString());
    }, [isExpanded, sectionKey]);

    // When sidebar is collapsed, show just the icon
    if (state === "collapsed") {
        return (
            <SidebarGroup>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            tooltip={title}
                            className="w-full justify-center"
                        >
                            <IconComponent className="h-4 w-4" />
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarGroup>
        );
    }

    // When sidebar is expanded, show collapsible section
    return (
        <SidebarGroup>
            <SidebarMenu>
                <SidebarMenuItem>
                    <SidebarMenuButton
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="w-full justify-between"
                    >
                        <div className="flex items-center gap-2">
                            <IconComponent className="h-4 w-4" />
                            <span>{title}</span>
                        </div>
                        {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                        )}
                    </SidebarMenuButton>
                    {isExpanded && (
                        <SidebarMenuSub>
                            {items.map((item) => {
                                if (item.name.startsWith('-')) {
                                    return (
                                        <SidebarMenuSubItem key={item.name}>
                                            <Separator className="my-2" />
                                        </SidebarMenuSubItem>
                                    );
                                }
                                const isActive = currentPath === item.href;
                                const ItemIcon = item.icon;
                                return (
                                    <SidebarMenuSubItem key={item.name}>
                                        <SidebarMenuSubButton
                                            isActive={isActive}
                                            onClick={() => navigate({ to: item.href })}
                                        >
                                            <ItemIcon className="h-4 w-4" />
                                            <span>{item.name}</span>
                                        </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                );
                            })}
                        </SidebarMenuSub>
                    )}
                </SidebarMenuItem>
            </SidebarMenu>
        </SidebarGroup>
    );
}

function SidebarItem({
    title,
    icon: IconComponent,
    items
}: SidebarItemProps) {
    const navigate = useNavigate();
    const routerState = useRouterState();
    const currentPath = routerState.location.pathname;

    // Create a unique key for this section based on title
    const sectionKey = `sidebar-section-${title}`;


    // When sidebar is expanded, show collapsible section
    return (
        <SidebarMenuItem>
            <SidebarMenuButton>
                {items.map((item) => {
                    if (item.name.startsWith('-')) {
                        return (
                            <SidebarMenuSubItem key={item.name}>
                                <Separator className="my-2" />
                            </SidebarMenuSubItem>
                        );
                    }
                    const isActive = currentPath === item.href;
                    const ItemIcon = item.icon;
                    return (
                        <SidebarMenuSubItem key={item.name}>
                            <SidebarMenuSubButton
                                isActive={isActive}
                                onClick={() => navigate({ to: item.href })}
                            >
                                <ItemIcon className="h-4 w-4" />
                                <span>{item.name}</span>
                            </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                    );
                })}
            </SidebarMenuButton>
        </SidebarMenuItem>
    );
}


export default function Layout({ children }: { children: React.ReactNode }) {
    const navigate = useNavigate();
    const routerState = useRouterState();
    const currentPath = routerState.location.pathname;
    const { t, isReady, locale, loading } = useI18n();
    const [currentUser, setCurrentUser] = useState<User | null>(() => {
        try {
            const cached = sessionStorage.getItem('current-user');
            return cached ? (JSON.parse(cached) as User) : null;
        } catch {
            return null;
        }
    });
    const [userLoaded, setUserLoaded] = useState(() => {
        try {
            return !!sessionStorage.getItem('current-user');
        } catch {
            return false;
        }
    });
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const sidebarContentRef = useRef<HTMLDivElement>(null);

    // Key for storing scroll position in sessionStorage
    const SIDEBAR_SCROLL_KEY = 'sidebar-scroll-position';

    // Fetch current user info
    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await apiFetch('/api/auth/me');
                if (response.ok) {
                    const userData = await response.json();
                    setCurrentUser(userData);
                    sessionStorage.setItem('current-user', JSON.stringify(userData));
                }
            } catch (error) {
                console.error('Error fetching user info:', error);
            } finally {
                setUserLoaded(true);
            }
        };

        fetchCurrentUser();
    }, []);

    // Keep Sentry user in sync with current user
    useEffect(() => {
        if (currentUser) {
            Sentry.setUser({
                id: currentUser.id,
                email: currentUser.email,
                username: currentUser.full_name,
                // extra context fields (allowed by Sentry)
                cost_center_id: currentUser.cost_center_id,
                is_superuser: currentUser.is_superuser,
                organization_id: currentUser.organization_id
            } as any);
            identifyPosthogUser({
                id: currentUser.id,
                email: currentUser.email,
                full_name: currentUser.full_name,
                is_superuser: currentUser.is_superuser,
                organization_id: currentUser.organization_id,
                organization_name: currentUser.organization_name,
            });
        } else {
            Sentry.setUser(null);
        }
    }, [currentUser]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (dropdownOpen) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [dropdownOpen]);

    // Restore scroll position on mount and when navigating
    useEffect(() => {
        const restoreScrollPosition = () => {
            const savedPosition = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
            if (savedPosition && sidebarContentRef.current) {
                const position = parseInt(savedPosition, 10);
                // Use requestAnimationFrame to ensure DOM is ready
                requestAnimationFrame(() => {
                    if (sidebarContentRef.current) {
                        sidebarContentRef.current.scrollTop = position;
                    }
                });
            }
        };

        // Restore on mount and route changes
        restoreScrollPosition();
    }, [currentPath]);

    // Save scroll position when scrolling
    useEffect(() => {
        const sidebarContent = sidebarContentRef.current;
        if (!sidebarContent) return;

        const handleScroll = () => {
            sessionStorage.setItem(SIDEBAR_SCROLL_KEY, sidebarContent.scrollTop.toString());
        };

        sidebarContent.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            sidebarContent.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const peopleModuleEnabled = useMemo(() => {
        if (!userLoaded) return false;
        if (currentUser?.module_people_enabled === false) return false;
        if (Array.isArray(currentUser?.modules)) {
            return currentUser.modules.includes('people');
        }
        return true;
    }, [userLoaded, currentUser?.module_people_enabled, currentUser?.modules]);

    const inventoryModuleEnabled = useMemo(() => {
        if (!userLoaded) return false;
        if (currentUser?.module_inventory_enabled === false) return false;
        if (Array.isArray(currentUser?.modules)) {
            return currentUser.modules.includes('inventory');
        }
        return true;
    }, [userLoaded, currentUser?.module_inventory_enabled, currentUser?.modules]);

    const menusModuleEnabled = useMemo(() => {
        if (!userLoaded) return false;
        if (currentUser?.module_menus_enabled === false) return false;
        if (Array.isArray(currentUser?.modules)) {
            return currentUser.modules.includes('menus');
        }
        return true;
    }, [userLoaded, currentUser?.module_menus_enabled, currentUser?.modules]);

    const expensesModuleEnabled = useMemo(() => {
        if (!userLoaded) return false;
        if (Array.isArray(currentUser?.modules)) {
            return currentUser.modules.includes('expenses');
        }
        return true;
    }, [userLoaded, currentUser?.modules]);

    const revenuesModuleEnabled = useMemo(() => {
        if (!userLoaded) return false;
        if (Array.isArray(currentUser?.modules)) {
            return currentUser.modules.includes('revenues');
        }
        return true;
    }, [userLoaded, currentUser?.modules]);

    const transactionsModuleEnabled = useMemo(() => {
        if (!userLoaded) return false;
        if (Array.isArray(currentUser?.modules)) {
            return currentUser.modules.includes('transactions');
        }
        return true;
    }, [userLoaded, currentUser?.modules]);

    const reportsModuleEnabled = useMemo(() => {
        if (!userLoaded) return false;
        if (Array.isArray(currentUser?.modules)) {
            return currentUser.modules.includes('reports');
        }
        return true;
    }, [userLoaded, currentUser?.modules]);

    const isOrgAdmin = useMemo(() => {
        if (!userLoaded) return false;
        if (currentUser?.is_superuser) return true;
        if (Array.isArray(currentUser?.roles) && currentUser.roles.includes('org_admin')) return true;
        if (Array.isArray(currentUser?.permissions) && currentUser.permissions.includes('org.settings.manage')) return true;
        return false;
    }, [userLoaded, currentUser?.is_superuser, currentUser?.roles, currentUser?.permissions]);

    const costsNavigation: NavigationItem[] = useMemo(() => {
        const items: NavigationItem[] = [
            /* { name: t('navigation.dashboard', 'common'), href: '/analytics', icon: ChartNoAxesCombinedIcon },*/
            { name: t('navigation.invoices', 'common'), href: '/invoices', icon: NewspaperIcon },
        ];
        // Only include people costs if people module is enabled
        if (peopleModuleEnabled) {
            items.push({ name: t('navigation.peopleCosts', 'common'), href: '/people/costs', icon: UsersFourIcon });
        }
        items.push(
            { name: t('navigation.taxes', 'common'), href: '/finance/taxes', icon: HandCoinsIcon },
            { name: t('navigation.costReports', 'common'), href: '/cost-reports', icon: ChartBarIcon },
        );
        return items;
    }, [t, peopleModuleEnabled]);

    const salesNavigation: NavigationItem[] = useMemo(() => [
        { name: t('navigation.invoices', 'common'), href: '/sales/invoices', icon: NewspaperIcon },
        { name: t('navigation.orders', 'common'), href: '/sales/orders', icon: ShoppingCartIcon },
        { name: t('navigation.products', 'common'), href: '/sales/products', icon: CubeIcon },
        { name: t('navigation.reports', 'common'), href: '/sales/dashboard', icon: ChartNoAxesCombinedIcon },
    ], [t]);

    const transactionsNavigation: NavigationItem[] = useMemo(() => [
        { name: t('navigation.payments', 'common'), href: '/payments', icon: BanknotesIcon },
        { name: t('navigation.reconciliation', 'common'), href: '/finance/reconciliation', icon: ArrowsRightLeftIcon },
        { name: t('navigation.bankTransactions', 'common'), href: '/bank-transactions', icon: BanknotesIcon },
    ], [t]);

    const reportsNavigation: NavigationItem[] = useMemo(() => [
        { name: t('navigation.dashboard', 'common'), href: '/dashboard', icon: ChartNoAxesCombinedIcon },
        { name: t('navigation.profitLoss', 'common'), href: '/profit-loss', icon: CurrencyDollarIcon },
        { name: t('navigation.dimensions', 'common'), href: '/metrics', icon: VectorTwo },
    ], [t])
    const inventoryNavigation: NavigationItem[] = useMemo(() => [
        { name: t('navigation.inventoryOverview', 'common'), href: '/inventory', icon: ArchiveBoxIcon },
        { name: t('navigation.newInventoryEntry', 'common'), href: '/inventory/new', icon: PlusIcon },
        { name: '-2', href: '#', icon: DivideIcon },
        { name: t('navigation.inventoryBatches', 'common'), href: '/inventory/batches', icon: ArchiveBoxArrowDownIcon },
        { name: t('navigation.receiveShipment', 'common'), href: '/inventory/batches/receive-shipment', icon: TruckIcon },
        { name: '-3', href: '#', icon: DivideIcon },
        { name: t('navigation.ingredients', 'common'), href: '/ingredients', icon: CubeIcon },
        { name: t('navigation.masterIngredients', 'common'), href: '/inventory/master-ingredients', icon: BeakerIcon },
        { name: t('navigation.transactionHistory', 'common'), href: '/inventory/transactions', icon: ArrowsRightLeftIcon },
    ], [t]);

    const na = [{ name: t('navigation.contracts', 'common'), href: '/people/contracts', icon: ClipboardDocumentListIcon }]

    const menusNavigation: NavigationItem[] = useMemo(() => [
        { name: t('navigation.menuItems', 'common'), href: '/menus', icon: BookOpenIcon },
    ], [t]);

    const peopleNavigation: NavigationItem[] = useMemo(() => [
        { name: t('navigation.employees', 'common'), href: '/people/employees', icon: UsersIcon },
        { name: t('navigation.employeeGroups', 'common'), href: '/people/groups', icon: UserGroupIcon },
        { name: t('navigation.schedulers', 'common'), href: '/people/schedulers', icon: CalendarIcon },
        { name: t('navigation.timesheets', 'common'), href: '/people/timesheets', icon: ClockIcon },
        { name: t('navigation.timeOff', 'common'), href: '/people/timeoff', icon: SunIcon },
    ], [t]);


    // Administration navigation - only for organization admins
    const adminNavigation: NavigationItem[] = useMemo(() => [
        { name: t('navigation.costCategories', 'common'), href: '/categories', icon: FolderIcon },
        { name: t('navigation.costCenters', 'common'), href: '/cost-centers', icon: BuildingStorefrontIcon },
        { name: t('navigation.userManagement', 'common'), href: '/admin/users', icon: UserIcon },
        { name: t('navigation.peopleSettings', 'common'), href: '/admin/people', icon: UsersIcon },
        { name: t('navigation.financeSettings', 'common'), href: '/admin/finance', icon: CurrencyDollarIcon },
        { name: t('tags.tags', 'common'), href: '/admin/tags', icon: TagIcon },
        { name: t('navigation.currencies', 'common'), href: '/admin/currencies', icon: CurrencyDollarIcon },
        { name: t('navigation.unitsOfMeasure', 'common'), href: '/units', icon: ScaleIcon },
        { name: t('navigation.localCategoryModels', 'common'), href: '/admin/local-category-models', icon: CpuChipIcon },
        { name: t('navigation.categoryManagement', 'common'), href: '/admin/category-management', icon: FolderIcon },
        { name: t('navigation.integrations', 'common'), href: '/admin/integrations', icon: LinkIcon },
        { name: t('navigation.dataImport', 'common'), href: '/admin/data-import', icon: ArrowDownTrayIcon },
    ], [t]);

    // Show loading state while translations are loading or user data is loading
    // This prevents rendering with missing translations
    if (!isReady || loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-lg">Loading...</div>
            </div>
        );
    }

    return (
        <SidebarProvider>
            <Sidebar variant="inset" collapsible="icon">
                <SidebarHeader className="">
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <div className="flex items-start gap-2 p-2 group-data-[collapsible=icon]:flex-col">
                                <SidebarMenuButton size="default" onClick={() => navigate({ to: '/home' })} tooltip="CafeMagic">
                                    <div className="flex aspect-square size-8 items-center justify-center bg-sidebar-primary text-sidebar-primary-foreground -ml-2">
                                        <CoffeeIcon weight="duotone" className="size-5" />
                                    </div>
                                    <div className="grid flex-1 text-left text-sm leading-tight">
                                        <span className="truncate font-semibold">
                                            {userLoaded ? currentUser?.organization_name : ''}
                                        </span>
                                    </div>
                                </SidebarMenuButton>
                                <SidebarTrigger className="size-8" />
                            </div>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarHeader>
                <SidebarContent ref={sidebarContentRef}>
                    <SidebarGroup>
                        <SidebarMenu>
                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    onClick={() => navigate({ to: '/invoices/gdrive-files' })}
                                    isActive={currentPath === '/invoices/gdrive-files'}
                                >
                                    <FolderIcon className="h-4 w-4" />
                                    <span>{t('navigation.gdriveFiles', 'common')}</span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                        </SidebarMenu>
                    </SidebarGroup>
                    {expensesModuleEnabled && (
                        <CollapsibleSidebarSection
                            title={t('navigation.costs', 'common')}
                            icon={CurrencyDollarIcon}
                            items={costsNavigation}
                            defaultExpanded={false}
                        />
                    )}
                    {revenuesModuleEnabled && (
                        <CollapsibleSidebarSection
                            title={t('navigation.revenues', 'common')}
                            icon={ShoppingCartIcon}
                            items={salesNavigation}
                            defaultExpanded={false}
                        />
                    )}
                    <SidebarGroup>

                            <SidebarMenuItem>
                                <SidebarMenuButton
                                    onClick={() => navigate({ to: '/partners' })}
                                    isActive={currentPath.startsWith('/partners')}
                                >
                                    <BuildingOfficeIcon className="h-4 w-4" />
                                    <span>{t('navigation.partners', 'common')}</span>
                                </SidebarMenuButton>
                            </SidebarMenuItem>
                    </SidebarGroup>

                    {transactionsModuleEnabled && (
                        <CollapsibleSidebarSection
                            title={t('navigation.transactions', 'common')}
                            icon={ShoppingCartIcon}
                            items={transactionsNavigation}
                            defaultExpanded={false}
                        />
                    )}

                    {reportsModuleEnabled && (
                        <CollapsibleSidebarSection
                            title={t('navigation.reports', 'common')}
                            icon={ChartBarIcon}
                            items={reportsNavigation}
                            defaultExpanded={false}
                        />
                    )}

                    {inventoryModuleEnabled && (
                        <CollapsibleSidebarSection
                            title={t('navigation.inventory', 'common')}
                            icon={ArchiveBoxIcon}
                            items={inventoryNavigation}
                            defaultExpanded={false}
                        />
                    )}

                    {menusModuleEnabled && (
                        <CollapsibleSidebarSection
                            title={t('navigation.menus', 'common')}
                            icon={BookOpenIcon}
                            items={menusNavigation}
                            defaultExpanded={false}
                        />
                    )}

                    {peopleModuleEnabled && (
                        <CollapsibleSidebarSection
                            title={t('navigation.people', 'common')}
                            icon={UsersIcon}
                            items={peopleNavigation}
                            defaultExpanded={false}
                        />
                    )}

                    {isOrgAdmin && (
                        <CollapsibleSidebarSection
                            title={t('navigation.administration', 'common')}
                            icon={Cog6ToothIcon}
                            items={adminNavigation}
                            defaultExpanded={false}
                        />
                    )}
                </SidebarContent>
                <SidebarFooter>
                    <SidebarMenu>
                        <SidebarMenuItem>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('Button clicked, current state:', dropdownOpen);
                                    setDropdownOpen(!dropdownOpen);
                                }}
                                className="flex w-full items-center gap-2 rounded-lg p-2 text-left text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                            >
                                <Avatar className="h-8 w-8 rounded-lg">
                                    <AvatarFallback className="rounded-lg">
                                        {currentUser?.email ? currentUser.email.substring(0, 2).toUpperCase() : 'U'}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">
                                        {currentUser?.email || 'User'}
                                    </span>
                                    <span className="truncate text-xs">
                                        {currentUser?.is_superuser ? 'Administrator' : 'User'}
                                    </span>
                                </div>
                                <ChevronDownIcon className="ml-auto size-4" />
                            </button>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarFooter>
                <SidebarRail />
            </Sidebar>
            <SidebarInset>
                <header className="flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-13 border-b mb-4 ">

                    <div className="flex-1"></div>
                    <div className="flex items-center gap-2 px-4">
                        <NotificationInbox />
                    </div>
                </header>
                <div className="flex flex-1 flex-col gap-6 p-6 pt-0">
                    {children}
                </div>
            </SidebarInset>

            {/* Dropdown Menu - Rendered outside sidebar to avoid clipping */}
            {dropdownOpen && (
                <div
                    className="fixed bottom-20 left-4 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] dark:bg-gray-800 dark:border-gray-700"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8 rounded-lg">
                                <AvatarFallback className="rounded-lg">
                                    {currentUser?.email ? currentUser.email.substring(0, 2).toUpperCase() : 'U'}
                                </AvatarFallback>
                            </Avatar>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-semibold">
                                    {currentUser?.email || 'User'}
                                </span>
                                <span className="truncate text-xs text-gray-500">
                                    {currentUser?.is_superuser ? 'Administrator' : 'User'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="py-1">
                        <button
                            onClick={() => {
                                navigate({ to: '/settings' });
                                setDropdownOpen(false);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <Cog6ToothIcon className="h-4 w-4" />
                            {t('navigation.settings')}
                        </button>
                        <button
                            onClick={() => {
                                logout();
                                setDropdownOpen(false);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <ArrowRightOnRectangleIcon className="h-4 w-4" />
                            Sign out
                        </button>
                    </div>
                </div>
            )}
        </SidebarProvider>
    );
}
